/**
 * /lyrics-writer — мастерская текста песни.
 *
 * Устроена вокруг одной мысли: текст песни отличается от стихотворения тем,
 * что его надо СПЕТЬ, а значит длину строки и рифму можно измерить. Всё
 * бесплатное здесь — измерение (слоги, схема рифмовки, разбор своего текста);
 * платное — только работа модели.
 *
 * ⚠️ Разбор считает СЕРВЕР, и это осознанно: правда о слогах живёт в словарях
 * (620 тыс. русских форм, 126 тыс. английских), которые в браузер не увезти.
 * Вторая, «быстрая» оценка в клиенте тут была бы не оптимизацией, а вторым
 * источником правды — он разъедется с первым и будет спорить с ним на экране.
 */
'use strict';

import { Api } from './api.js';
import { createGutters } from './gutters.js';

const root = document.getElementById('lwRoot');
if (root) init();

function init() {
    const T = (window.__LW && window.__LW.t) || {};
    const CFG = {
        analyze: root.dataset.analyze,
        rhymes: root.dataset.rhymes,
        compose: root.dataset.compose,
        section: root.dataset.section,
        song: root.dataset.song,
        login: root.dataset.login,
        buy: root.dataset.buy,
    };
    const FORMS = (window.__LW && window.__LW.forms) || {};
    const COST = (window.__LW && window.__LW.cost) || {};
    const LANGS = (window.__LW && window.__LW.langNames) || {};

    const $ = (id) => document.getElementById(id);
    const text = $('lwText');
    const mirror = $('lwMirror');
    const gut = createGutters(text, mirror, $('lwScheme'), $('lwSylGutter'));

    const el = {
        lang: $('lwLang'), form: $('lwForm'), rhyme: $('lwRhyme'), syl: $('lwSyl'),
        theme: $('lwTheme'), mood: $('lwMood'), genre: $('lwGenre'),
        kw: $('lwKw'), avoid: $('lwAvoid'), rhymeWith: $('lwRhymeWith'), clean: $('lwClean'),
        go: $('lwGo'), goLabel: $('lwGoLabel'), cost: $('lwCost'), status: $('lwStatus'),
        report: $('lwReport'), reportCount: $('lwReportCount'), issues: $('lwIssues'),
        sections: $('lwSections'), count: $('lwCount'), kind: $('lwKind'),
        rhymeNote: $('lwRhymeNote'), rhymeWord: $('lwRhymeWord'), rhymeList: $('lwRhymeList'),
        undo: $('lwUndo'),
    };

    let pov = 'free';   // «не важно» — умолчание: см. замер анафоры в LyricsPrompt
    let lastAnalysis = null;
    let previous = null;          // для «Вернуть» после перезаписи моделью
    let busy = false;

    // ── восстановление черновика ────────────────────────────────────────────
    // Потеря написанного — жалоба №1 у ближайшего аналога (Rhymer's Block:
    // «зарегистрировался — и все мои песни пропали»). Черновик живёт локально
    // и переживает перезагрузку без всякого аккаунта.
    const KEY = 'lw:draft';
    try {
        const saved = localStorage.getItem(KEY);
        if (saved && !text.value) text.value = saved;
    } catch (_) {}

    function saveDraft() {
        try { localStorage.setItem(KEY, text.value); } catch (_) {}
    }

    // ── настройки запоминаются ──────────────────────────────────────────────
    if (window.__toolSettings) {
        const S = window.__toolSettings;
        S.bind('lyrics-writer', el.lang, 'lang', el.lang.value);
        S.bind('lyrics-writer', el.form, 'form', 'simple');
        S.bind('lyrics-writer', el.rhyme, 'rhyme', el.rhyme.value);
        S.bind('lyrics-writer', el.syl, 'syl', '8');
        S.bind('lyrics-writer', el.genre, 'genre', '');
    }

    // ── язык ────────────────────────────────────────────────────────────────
    function langOpt() { return el.lang.options[el.lang.selectedIndex]; }

    function applyLang(initial) {
        const opt = langOpt();
        const kind = opt ? opt.dataset.kind : 'rules';
        const nativeRhyme = opt ? opt.dataset.rhyme : 'abab';
        const name = LANGS[el.lang.value] || el.lang.value;

        el.kind.textContent = kind === 'dictionary' ? T.rp_dict
            : kind === 'rules' ? T.rp_rules
            : (T.rp_none || '').replace(':lang', name);

        // Языку, где конечная рифма не приём, схема не навязывается — но
        // выбрать её руками по-прежнему можно.
        if (nativeRhyme === 'free') {
            el.rhymeNote.textContent = (T.rhyme_free_note || '').replace(':lang', name);
            el.rhymeNote.hidden = false;
            if (initial) el.rhyme.value = 'free';
        } else {
            el.rhymeNote.hidden = true;
        }
        showDemo(el.lang.value);
        schedule();
        if (el.rhymeWord.value.trim()) lookupRhymes();
    }
    el.lang.addEventListener('change', () => applyLang(false));

    /*
     * Витрина. Разбор в ней посчитан НА СЕРВЕРЕ и уже лежит в разметке — JS
     * только показывает блок нужного языка. Это намеренно: страница обязана
     * доказывать своё обещание сразу, без запроса и даже без скриптов.
     */
    const demo = $('lwDemo');

    function showDemo(lang) {
        if (!demo) return;
        let shown = null;
        demo.querySelectorAll('[data-demo]').forEach((b) => {
            const on = b.dataset.demo === lang;
            b.hidden = !on;
            if (on) shown = b;
        });
        // Языка без примера быть не должно (сторож LyricsExamplesAreCleanTest),
        // но если он появится — честнее убрать панель, чем показать чужой язык.
        demo.hidden = !shown;
        const badge = $('lwDemoLang');
        if (badge) badge.textContent = LANGS[lang] || lang;
    }

    document.querySelectorAll('[data-use]').forEach((b) => {
        b.addEventListener('click', () => {
            const block = b.closest('[data-demo]');
            const lines = block ? block.querySelectorAll('.lw-demo-tag, .lw-demo-t') : [];
            let out = '';
            lines.forEach((n) => {
                const t = n.textContent.trim();
                out += (n.classList.contains('lw-demo-tag') ? (out ? '\n' + t : t) : t) + '\n';
            });
            previous = text.value;
            el.undo.hidden = false;
            text.value = out.trim();
            saveDraft();
            schedule();
            // Пример уступает место работе: свёрнутая витрина не спорит с
            // редактором за экран.
            demo.open = false;
            text.focus();
            text.setSelectionRange(0, 0);
        });
    });

    // ── состав заказа ───────────────────────────────────────────────────────
    function structure() {
        return FORMS[el.form.value] || FORMS.simple || ['verse', 'chorus', 'verse', 'chorus'];
    }

    function spec() {
        return {
            lang: el.lang.value,
            theme: el.theme.value.trim(),
            structure: structure(),
            rhyme: el.rhyme.value,
            syllables: el.syl.value ? parseInt(el.syl.value, 10) : null,
            mood: el.mood.value.trim(),
            genre: el.genre.value.trim(),
            pov: pov,
            keywords: el.kw.value.trim(),
            avoid: el.avoid.value.trim(),
            rhyme_with: el.rhymeWith.value.trim(),
            clean: !!el.clean.checked,
        };
    }

    document.querySelectorAll('#lwPov .tc-seg-b').forEach((b) => {
        b.addEventListener('click', () => {
            pov = b.dataset.pov;
            document.querySelectorAll('#lwPov .tc-seg-b').forEach((x) => x.classList.toggle('on', x === b));
        });
    });

    // ── разбор ──────────────────────────────────────────────────────────────
    let timer = null;

    function schedule() {
        if (timer) clearTimeout(timer);
        timer = setTimeout(analyze, 420);
    }

    async function analyze() {
        const value = text.value;
        el.count.textContent = (T.ed_lines || ':n').replace(
            ':n', value.split('\n').filter((l) => l.trim() && !/^\[[^\]]*\]$/.test(l.trim())).length
        );
        if (!value.trim()) {
            gut.clear();
            el.report.hidden = true;
            return;
        }
        const data = await Api.analyze(CFG, Object.assign(spec(), { text: value }));
        if (!data) return;
        lastAnalysis = data;
        paint(data, value);
    }

    /** Разложить ответ сервера обратно по строкам текстового поля. */
    function paint(data, value) {
        const lines = value.split('\n');
        const rows = [];
        const lineMap = [];

        // Сервер отдаёт поющиеся строки по порядку; сопоставляем их с
        // логическими строками поля, пропуская маркеры и пустые.
        const sung = data.report.lines || [];
        const scheme = data.scheme || [];
        let si = 0;          // индекс поющейся строки
        let sec = -1;        // индекс секции
        let inSection = false;

        // какие строки названы дефектными
        const badLines = {};
        (data.report.issues || []).forEach((i) => {
            if (i.line !== null && i.line !== undefined) badLines[i.line] = true;
        });

        for (let i = 0; i < lines.length; i++) {
            const t = lines[i].trim();
            if (!t) { lineMap.push(null); continue; }
            if (/^\[[^\]]*\]$/.test(t)) { sec++; inSection = true; lineMap.push(null); continue; }
            if (!inSection) { sec = Math.max(sec, 0); inSection = true; }
            const row = sung[si];
            if (!row) { lineMap.push(null); continue; }
            const letters = scheme[row.section] || '';
            const letter = letters.charAt(row.index) || '';
            rows.push({
                syl: row.syl,
                letter: letter === '-' ? '' : letter,
                family: letter && letter !== '-' ? letter.charCodeAt(0) - 65 : 0,
                bad: !!badLines[row.n],
            });
            lineMap.push(rows.length - 1);
            si++;
        }
        gut.render(rows, lineMap);
        renderReport(data);
        // Счётчик законченных разборов. Нужен сторожу: «колонка не пуста» —
        // НЕ признак свежести, и тест, который ждёт присутствия, проходит на
        // числах от прошлого текста (поймано на себе).
        root.dataset.rev = String((parseInt(root.dataset.rev || '0', 10) || 0) + 1);
    }

    function renderReport(data) {
        const issues = data.report.issues || [];
        el.report.hidden = false;
        el.reportCount.textContent = issues.length
            ? (T.rp_issues || ':n').replace(':n', issues.length)
            : '';
        el.issues.innerHTML = '';
        if (!issues.length) {
            const ok = document.createElement('p');
            ok.className = 'lw-ok';
            ok.textContent = T.rp_clean || '';
            el.issues.appendChild(ok);
        } else {
            issues.forEach((i) => {
                const b = document.createElement('button');
                b.type = 'button';
                b.className = 'lw-issue';
                b.textContent = i.say;
                // Клик по замечанию ставит курсор на ту самую строку. Прокрутка
                // только 'nearest' — видимую строку она не двигает.
                b.addEventListener('click', () => focusLine(i.line));
                el.issues.appendChild(b);
            });
        }

        // Чипы секций с перегенерацией по одной — чего нет ни у кого.
        el.sections.innerHTML = '';
        (data.report.sections || []).forEach((s, idx) => {
            const chip = document.createElement('span');
            chip.className = 'lw-sec';
            const label = document.createElement('b');
            label.textContent = (s.label || s.tag);
            chip.appendChild(label);
            const n = document.createElement('span');
            n.textContent = (data.scheme && data.scheme[idx]) ? data.scheme[idx] : '';
            chip.appendChild(n);
            const go = document.createElement('button');
            go.type = 'button';
            go.textContent = '↻';
            go.title = T.ed_regen || '';
            go.setAttribute('aria-label', (T.ed_regen || '') + ' — ' + (s.label || s.tag));
            go.addEventListener('click', () => regenSection(idx));
            chip.appendChild(go);
            el.sections.appendChild(chip);
        });
    }

    function focusLine(n) {
        if (n === null || n === undefined) return;
        const lines = text.value.split('\n');
        let sung = -1;
        let pos = 0;
        for (let i = 0; i < lines.length; i++) {
            const t = lines[i].trim();
            if (t && !/^\[[^\]]*\]$/.test(t)) {
                sung++;
                if (sung === n) {
                    text.focus();
                    text.setSelectionRange(pos, pos + lines[i].length);
                    // ⛔ Никакой прокрутки страницы: 'nearest' — no-op для того,
                    // что и так видно (правило проекта).
                    text.scrollIntoView({ block: 'nearest' });
                    return;
                }
            }
            pos += lines[i].length + 1;
        }
    }

    // ── ввод ────────────────────────────────────────────────────────────────
    text.addEventListener('input', () => { saveDraft(); schedule(); });
    window.addEventListener('resize', () => { if (lastAnalysis) paint(lastAnalysis, text.value); }, { passive: true });

    document.querySelectorAll('.lw-tag[data-tag]').forEach((b) => {
        b.addEventListener('click', () => {
            const tag = '[' + b.dataset.tag + ']';
            const at = text.selectionStart;
            const before = text.value.slice(0, at);
            const after = text.value.slice(text.selectionEnd);
            const pad = before && !before.endsWith('\n\n') ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
            text.value = before + pad + tag + '\n' + after;
            const caret = (before + pad + tag + '\n').length;
            text.focus();
            text.setSelectionRange(caret, caret);
            saveDraft();
            schedule();
        });
    });

    $('lwClear').addEventListener('click', () => {
        if (text.value.trim() && !window.confirm(T.ed_clear || 'Clear?')) return;
        previous = text.value;
        el.undo.hidden = false;
        text.value = '';
        saveDraft();
        gut.clear();
        el.report.hidden = true;
        el.count.textContent = (T.ed_lines || ':n').replace(':n', 0);
    });

    el.undo.addEventListener('click', () => {
        if (previous === null) return;
        text.value = previous;
        previous = null;
        el.undo.hidden = true;
        saveDraft();
        schedule();
    });

    // ── рифмы ───────────────────────────────────────────────────────────────
    let rhymeTimer = null;
    el.rhymeWord.addEventListener('input', () => {
        if (rhymeTimer) clearTimeout(rhymeTimer);
        rhymeTimer = setTimeout(lookupRhymes, 380);
    });

    async function lookupRhymes() {
        const w = el.rhymeWord.value.trim();
        el.rhymeList.innerHTML = '';
        if (!w) return;
        const data = await Api.rhymes(CFG, w, el.lang.value);
        if (!data) return;
        if (!data.has_index) {
            const p = document.createElement('p');
            p.className = 'tc-hint';
            // «Пусто» и «индекса нет» — разные вещи, и человек обязан знать
            // какая. Иначе он решит, что его слово не рифмуется ни с чем.
            p.textContent = (T.rh_no_index || '').replace(':lang', LANGS[el.lang.value] || el.lang.value);
            el.rhymeList.appendChild(p);
            return;
        }
        if (!data.rhymes.length) {
            const p = document.createElement('p');
            p.className = 'tc-hint';
            p.textContent = T.rh_none || '';
            el.rhymeList.appendChild(p);
            return;
        }
        data.rhymes.forEach((word) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'lw-rw';
            b.textContent = word;
            b.addEventListener('click', () => {
                const at = text.selectionStart;
                text.value = text.value.slice(0, at) + word + text.value.slice(text.selectionEnd);
                text.focus();
                text.setSelectionRange(at + word.length, at + word.length);
                saveDraft();
                schedule();
            });
            el.rhymeList.appendChild(b);
        });
    }

    // ── платный лейн ────────────────────────────────────────────────────────
    function cost(kind) { return COST[kind] || 0; }

    el.cost.textContent = (T.ws_cost || '').replace(':n', cost('song'));

    function say(msg, kind) {
        el.status.textContent = msg || '';
        el.status.style.color = kind === 'warn' ? 'var(--warning,#e0a642)' : '';
    }

    function explain(r) {
        const j = r.json || {};
        if (r.status === 401) {
            // Фраза объясняет, ССЫЛКА зовёт. Раньше сюда уходил один и тот же
            // текст, и человек читал его дважды подряд.
            say(T.err_auth, 'warn');
            link(CFG.login, T.err_auth_cta);
            return;
        }
        if (r.status === 402) {
            say((T.err_tokens || '').replace(':need', j.need || '?').replace(':balance', j.balance || 0), 'warn');
            link(CFG.buy, T.topup);
            return;
        }
        if (r.status === 422 && j.error === 'blocked') { say(T.err_blocked, 'warn'); return; }
        if (r.status === 422) { say(T.err_theme, 'warn'); return; }
        say(T.err_failed, 'warn');
    }

    function link(href, label) {
        const a = document.createElement('a');
        a.href = href;
        a.className = 'lw-tag';
        a.style.marginInlineStart = '8px';
        a.textContent = label;
        el.status.appendChild(document.createTextNode(' '));
        el.status.appendChild(a);
    }

    async function run(kind, payload) {
        if (busy) return;
        busy = true;
        el.go.disabled = true;
        el.goLabel.textContent = T.ws_generating || '…';
        say('');
        try {
            const r = kind === 'song' ? await Api.compose(CFG, payload) : await Api.section(CFG, payload);
            if (r.status !== 200 || !r.json || !r.json.ok) { explain(r); return; }
            previous = text.value;
            el.undo.hidden = false;
            text.value = r.json.text;
            saveDraft();
            if (window.__ae) {
                window.__ae('tool_complete', {
                    slug: 'lyrics-writer', kind: kind, lang: payload.lang,
                    drafts: r.json.drafts, repairs: r.json.repairs,
                    score: r.json.report && r.json.report.score,
                    issues: r.json.report ? (r.json.report.issues || []).length : null,
                });
            }
            if (r.json.report) { lastAnalysis = { report: r.json.report, scheme: [] }; }
            analyze();
        } catch (_) {
            say(T.err_network, 'warn');
        } finally {
            busy = false;
            el.go.disabled = false;
            el.goLabel.textContent = T.ws_generate || '';
        }
    }

    el.go.addEventListener('click', () => {
        const s = spec();
        if (!s.theme) { say(T.err_theme, 'warn'); el.theme.focus(); return; }
        run('song', s);
    });

    function regenSection(index) {
        const s = spec();
        s.text = text.value;
        s.index = index;
        run('section', s);
    }

    // ── выдача ──────────────────────────────────────────────────────────────
    $('lwCopy').addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText(text.value);
            if (window.__toast) window.__toast(T.ex_copied, 'success');
        } catch (_) {
            if (window.__toast) window.__toast(T.err_failed, 'error');
        }
    });

    $('lwTxt').addEventListener('click', () => {
        const blob = new Blob([text.value], { type: 'text/plain;charset=utf-8' });
        const name = (window.__outputName && window.__outputName.sanitize)
            ? window.__outputName.sanitize(firstLine() || 'lyrics') + '.txt'
            : 'lyrics.txt';
        if (window.__downloadFile) window.__downloadFile(blob, name);
    });

    $('lwPdf').addEventListener('click', () => {
        // ⚠️ Текст песни — это то, что человек понесёт на репетицию, поэтому у
        // него обязан быть формат, который открывается сам. Машинный .txt —
        // вторая кнопка, не единственная.
        if (!window.__generatePDF) { if (window.__toast) window.__toast(T.err_failed, 'error'); return; }
        const card = document.createElement('div');
        card.style.cssText = 'padding:28px 32px;background:#fff;color:#111;font-family:Georgia,serif';
        const h = document.createElement('h1');
        h.style.cssText = 'font-size:20px;margin:0 0 14px';
        h.textContent = firstLine() || document.title;
        card.appendChild(h);
        const pre = document.createElement('div');
        pre.style.cssText = 'white-space:pre-wrap;font-size:14px;line-height:1.7';
        pre.textContent = text.value;
        card.appendChild(pre);
        document.body.appendChild(card);
        card.style.position = 'fixed';
        card.style.left = '-10000px';
        card.style.width = '720px';
        window.__generatePDF({ element: card, filename: (firstLine() || 'lyrics') + '.pdf' })
            .catch(() => { if (window.__toast) window.__toast(T.err_failed, 'error'); })
            .finally(() => card.remove());
    });

    function firstLine() {
        const l = text.value.split('\n').map((x) => x.trim())
            .filter((x) => x && !/^\[[^\]]*\]$/.test(x));
        return l.length ? l[0].slice(0, 48) : '';
    }

    /*
     * «Сделать песню». Ни Suno, ни Udio не отдают текст файлом вовсе — а мы
     * отдаём И файл, И переход.
     *
     * ⚠️ Ключ моста — СУЩЕСТВУЮЩИЙ `__handoff_prompt_to_song_v1`, который
     * /song-generator уже читает (applyPromptHandoff, принимает {style,lyrics}).
     * Свой ключ выглядел бы работающим и не делал бы ничего: человек попадал
     * бы в генератор с пустым полем текста. Проверено — первая версия этого
     * кода клала «sg:lyrics», которого не читает никто.
     */
    const toSong = $('lwToSong');
    toSong.addEventListener('click', (e) => {
        e.preventDefault();
        try {
            sessionStorage.setItem('__handoff_prompt_to_song_v1', JSON.stringify({
                lyrics: text.value,
                style: el.genre.value.trim(),
            }));
        } catch (_) {}
        window.location.href = CFG.song;
    });

    applyLang(true);
    schedule();
}
